import { Box, createStyles, Divider, Grid, Popover, Tab, Tabs } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Checkbox,
  CustomPalette,
  FormControlLabel,
  Icons,
  Typography,
} from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import type { InspectionList as InspectionListType } from '../../rxdb/inspectionList/inspectionList.types';
import type { TaskList } from '../../rxdb/taskList/taskList.types';
import type { ViewInspectorDocument } from '../../rxdb/viewInspector/viewInspector.types';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { BulkButtons, FieldInspectorTab, LeadInspectorTab } from '../../utilities/enums';
import { getValueFromLocalStorage, setValueinLocalStorage } from '../../utilities/utilityFunctions';
import AssignInspector from '../AssignInspector';
import AdhocBulkInspectionReAssignment from '../AssignInspector/AdhocBulkInspectionReAssignment';
import BulkReAssignInspector from '../AssignInspector/BulkReAssignInspector';
import AssignToMeDialog from '../AssignToMeDialog';
import BulkSignAndStamp from '../BulkSignAndStamp';
import InspectionListSortBy from '../InspectionListSortBy';
import OfflineDialog from '../OfflineDialog';
import ViewInspectors from '../ViewInspectors';

const useStyles = makeStyles(() =>
  createStyles({
    sortByIcon: {
      verticalAlign: 'middle',
      marginLeft: 10,
    },
    dividerGrid: {
      marginRight: 16,
      height: 32,
      alignSelf: 'center',
    },
    dividerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
    },
    allInspectionGrid: {
      display: 'flex',
      marginLeft: 'auto',
      marginRight: 16,
    },
    sortByStyle: {
      cursor: 'pointer',
      padding: 0,
    },
    bulkUploadButtonGrid: {
      '& div > button': {
        width: 'fit-content',
        height: '32px',
      },
    },
    assignButton: {
      alignSelf: 'center',
    },
    xFeatherGrid: {
      alignSelf: 'center',
      marginRight: '16px',
      display: 'flex',
      cursor: 'pointer',
    },
    dividerForGesture: {
      height: 20,
      alignSelf: 'center',
      margin: '0px 16px',
      background: 'rgba(255,255,255,0.3)',
    },
    selected: {
      color: 'rgba(255, 255, 255, .38)',
      alignSelf: 'center',
    },
    checkbox: {
      paddingLeft: '10px',
    },
    tabs: {
      minWidth: '125px',
      paddingTop: 0,
      paddingBottom: 0,
    },
    selectAllLabel: {
      margin: 4,
    },
    sortByGrid: {
      placeSelf: 'center',
    },
    bulkButtonName: {
      marginLeft: 'auto',
    },
    inspectionGridMarauto: {
      marginLeft: 'auto',
    },
    allInspectionGridMarzero: {
      marginLeft: '0px',
    },
    actionButtonGrid: {
      '& .actionButton': {
        letterSpacing: '1.25px',
      },
    },
    paper: {
      maxWidth: '170px',
      minWidth: '170px',
      overflow: 'hidden',
      marginLeft: '-35px',
      marginTop: '5px',
      backgroundColor: CustomPalette.elevation.dp24Solid,
    },
    button: {
      padding: '10px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.09)',
      },
    },
  }),
);

interface Props {
  tabValue: number;
  setValue: (tabValue: number) => void;
  allInspectionCheck: boolean;
  setAllInspectionCheck: (e: boolean) => void;
  assignedToMe: boolean;
  setAssignedToMe: (e: boolean) => void;
  inspectionId: string;
  inspectionName: string;
  needAttention: string;
  inspectionLevel: string;
  handleInspectionIdClick: () => void;
  handleInspectionNameClick: () => void;
  handleNeedAttentionClick: () => void;
  handleInspectionLevelClick: () => void;
  resetSortBy: () => void;
  resetFilters: () => void;
  countOfInspectionOnTab: {
    unAssigned: number;
    onGoing: number;
    approval: number;
  };
  handleSelectAll: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inspectorData: ViewInspectorDocument[];
  taskList: TaskList[];
  setSelectedTab: (e: string) => void;
  checkInspectionList: boolean;
  inspectionList: InspectionListType[];
  setInitialLoader: (e: boolean) => void;
  isAssignToLead: boolean;
}

export function TabPanel(props: {
  children: PropTypes.ReactNodeLike;
  value: number;
  index: number;
}) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const InspectionListTabs: React.FC<Props> = ({
  tabValue,
  setValue,
  allInspectionCheck,
  setAllInspectionCheck,
  assignedToMe,
  setAssignedToMe,
  inspectionId,
  inspectionName,
  needAttention,
  inspectionLevel,
  handleInspectionIdClick,
  handleInspectionNameClick,
  handleNeedAttentionClick,
  handleInspectionLevelClick,
  resetSortBy,
  resetFilters,
  countOfInspectionOnTab,
  handleSelectAll,
  inspectorData,
  taskList,
  setSelectedTab,
  checkInspectionList,
  inspectionList,
  setInitialLoader,
  isAssignToLead,
}) => {
  const classes = useStyles();
  const { HandleGesture, CurrentUserSelected } = useMst();
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const [openFIViewInspectors, setOpenFIViewInspectors] = useState<boolean>(false);
  const [openAddFieldInspector, setOpenAddFieldInspector] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const id = open ? 'inspection-list-popover-' : undefined;
  const [openAssignToMeDialog, setOpenAssignToMeDialog] = useState<boolean>(false);
  const [openReAssignInspector, setOpenReAssignInspector] = useState(false);
  const [openBulkSignAndStamp, setOpenBulkSignAndStamp] = useState<boolean>(false);
  const [taskIds, setTaskIds] = useState<number[]>([]);
  const [openAssignInspector, setOpenAssignInspector] = useState<boolean>(false);
  const [offlineDialogBox, setOfflineDialogBox] = useState<boolean>(false);
  const [actionAnchorEl, setActionAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isAdhoc = CurrentUserSelected.isAdhoc;
  const online = useOnlineStatus();

  const changeTab = (event: React.ChangeEvent<unknown>, newValue: number) => {
    setSelectedTab((event.currentTarget as HTMLElement)?.innerText);
    setValueinLocalStorage(
      isAdhoc ? 'adhocInspectionTabValue' : 'inspectionTabValue',
      String(newValue),
    );
    setValue(newValue);
    resetSortBy();
    resetFilters();
    setInitialLoader(tabValue !== newValue);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget as HTMLInputElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReAssign = (e: { stopPropagation: () => void }) => {
    setTaskIds(HandleGesture.taskIds.filter(i => i !== undefined));
    setOpenReAssignInspector(true);
    e.stopPropagation();
  };

  const getBulkUploadButtonName = () => {
    if (HandleGesture.gestureForSignAndStamp) return BulkButtons.SIGN_AND_STAMP;
    if (HandleGesture.gestureForReAssign) return BulkButtons.RE_ASSIGN;
    if (isAssignToLead) return BulkButtons.ASSIGN;
    return BulkButtons.ASSIGN_TO_ME;
  };

  const handleBulkAssignClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (online) {
      if (HandleGesture.gestureForSignAndStamp) setOpenBulkSignAndStamp(true);
      else if (HandleGesture.gestureForReAssign) handleReAssign(e);
      else if (isAssignToLead) setOpenAssignInspector(true);
      else setOpenAssignToMeDialog(true);
    } else {
      setOfflineDialogBox(true);
    }
  };

  const handleReAssignOrSignAndStampClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>,
    isReassign: boolean,
  ) => {
    setActionAnchorEl(null);
    if (online) {
      if (isReassign) handleReAssign(event);
      else setOpenBulkSignAndStamp(true);
    } else {
      setOfflineDialogBox(true);
    }
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
  ) => {
    setActionAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleActionClose = (e: { stopPropagation: () => void }) => {
    setActionAnchorEl(null);
    e.stopPropagation();
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} container alignItems="center">
          <Box sx={{ width: 'fit-content' }}>
            {HandleGesture.bulkUpload ? (
              <Grid item container className={classes.checkbox}>
                <FormControlLabel
                  control={
                    <Checkbox
                      data-testid="select-all-checkbox"
                      checked={HandleGesture.selectAll}
                      onChange={handleSelectAll}
                    />
                  }
                  label="Select All"
                  className={classes.selectAllLabel}
                />
                <Grid item className={classes.dividerForGesture}>
                  <Divider orientation="vertical" className={classes.dividerStyle} />
                </Grid>
                <Typography fontWeight="normal" className={classes.selected}>
                  {`${Math.max(
                    HandleGesture.inspectionIds.length,
                    HandleGesture.taskIds.length,
                  )} Selected`}
                </Typography>
              </Grid>
            ) : (
              <Box sx={{ borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={changeTab}
                  aria-label="basic tabs example"
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab
                    label={
                      isAssignToLead
                        ? `Un Assigned (${countOfInspectionOnTab.unAssigned})`
                        : `On Going (${countOfInspectionOnTab.onGoing})`
                    }
                    {...a11yProps(0)}
                    className={classes.tabs}
                  />
                  <Tab
                    data-testid="ongoing-tab"
                    label={
                      isAssignToLead
                        ? `On Going (${countOfInspectionOnTab.onGoing})`
                        : `Approval (${countOfInspectionOnTab.approval})`
                    }
                    {...a11yProps(1)}
                    className={classes.tabs}
                  />
                  {!isAdhoc && (
                    <Tab
                      data-testid="approval-tab"
                      label={
                        isAssignToLead
                          ? `Approval (${countOfInspectionOnTab.approval})`
                          : `Un Assigned (${countOfInspectionOnTab.unAssigned})`
                      }
                      {...a11yProps(2)}
                      className={classes.tabs}
                    />
                  )}
                </Tabs>
              </Box>
            )}
          </Box>
          {((tabValue === LeadInspectorTab.UNASSIGN && isAssignToLead) ||
            HandleGesture.bulkUpload) && <div className={classes.bulkButtonName} />}
          {HandleGesture.bulkUpload ? (
            <>
              <Grid className={classes.bulkUploadButtonGrid} item>
                {((HandleGesture.gestureForReAssign && !HandleGesture.gestureForSignAndStamp) ||
                  (!HandleGesture.gestureForReAssign && HandleGesture.gestureForSignAndStamp) ||
                  HandleGesture.gestureForUnAssigned) && (
                  <Button
                    data-testid="bulk-upload-button"
                    color="primary"
                    className={classes.assignButton}
                    noMinHeight
                    onClick={handleBulkAssignClick}
                    disabled={!HandleGesture.inspectionIds.length && !HandleGesture.taskIds.length}
                  >
                    <Typography variant="subtitle2">{getBulkUploadButtonName()}</Typography>
                  </Button>
                )}
                {HandleGesture.gestureForReAssign && HandleGesture.gestureForSignAndStamp && (
                  <>
                    <Grid
                      container
                      justifyContent="flex-end"
                      alignItems="flex-end"
                      className={classes.actionButtonGrid}
                    >
                      <Button
                        color="primary"
                        className="actionButton"
                        noMinHeight
                        onClick={openActionMenu}
                        endAdornment={<Icons.ChevronDownFeather color="primary" size="small" />}
                      >
                        <Typography variant="subtitle2">Action</Typography>
                      </Button>
                    </Grid>
                    <Popover
                      id="action"
                      open={Boolean(actionAnchorEl)}
                      anchorEl={actionAnchorEl}
                      onClose={handleActionClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                      classes={{ paper: classes.paper }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid
                            container
                            className={classes.button}
                            onMouseUp={e => handleReAssignOrSignAndStampClick(e, true)}
                            onMouseDown={e => e.stopPropagation()}
                            onTouchStart={e => e.stopPropagation()}
                            onTouchEnd={e => handleReAssignOrSignAndStampClick(e, true)}
                          >
                            <Typography variant="subtitle2">Re-Assign</Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Grid
                            container
                            className={classes.button}
                            onMouseUp={e => handleReAssignOrSignAndStampClick(e, false)}
                            onMouseDown={e => e.stopPropagation()}
                            onTouchStart={e => e.stopPropagation()}
                            onTouchEnd={e => handleReAssignOrSignAndStampClick(e, false)}
                          >
                            <Typography variant="subtitle2">Sign & Stamp</Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Popover>
                  </>
                )}
              </Grid>

              <Grid item className={classes.dividerForGesture}>
                <Divider orientation="vertical" className={classes.dividerStyle} />
              </Grid>

              <Grid item className={classes.xFeatherGrid}>
                <Icons.XFeather color="primary" onClick={() => HandleGesture.resetEverything()} />
              </Grid>
            </>
          ) : (
            <Grid
              className={
                isAssignToLead
                  ? tabValue === LeadInspectorTab.ONGOING || tabValue === LeadInspectorTab.APPROVAL
                    ? classes.allInspectionGrid
                    : classes.allInspectionGridMarzero
                  : classes.allInspectionGrid
              }
            >
              {!isAssignToLead
                ? tabValue !== FieldInspectorTab.UNASSIGN && (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allInspectionCheck}
                            onChange={e => {
                              setAllInspectionCheck(e.target.checked);
                            }}
                            data-testid="allInspectionCheck"
                          />
                        }
                        label="All Inspections"
                      />
                      <Grid hidden={checkInspectionList} item className={classes.dividerGrid}>
                        <Divider orientation="vertical" className={classes.dividerStyle} />
                      </Grid>
                    </>
                  )
                : (tabValue === LeadInspectorTab.ONGOING ||
                    tabValue === LeadInspectorTab.APPROVAL) && (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={assignedToMe}
                            onChange={e => {
                              setAssignedToMe(e.target.checked);
                            }}
                            data-testid="assignedToMe"
                          />
                        }
                        label="Assigned to me"
                      />
                      <Grid hidden={checkInspectionList} item className={classes.dividerGrid}>
                        <Divider orientation="vertical" className={classes.dividerStyle} />
                      </Grid>
                    </>
                  )}
              <Grid
                hidden={checkInspectionList}
                aria-describedby={id}
                id="sort-by-deficiency"
                item
                onClick={handleClick}
                className={classes.sortByGrid}
              >
                <Typography fontWeight={700} color="primary" className={classes.sortByStyle}>
                  Sort By
                  {open ? (
                    <Icons.ChevronUpFeather color="primary" className={classes.sortByIcon} />
                  ) : (
                    <Icons.ChevronDownFeather color="primary" className={classes.sortByIcon} />
                  )}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      {openFIViewInspectors && (
        <div>
          <ViewInspectors
            openViewInspectors={openFIViewInspectors}
            setOpenViewInspectors={setOpenFIViewInspectors}
            openAddFieldInspector={openAddFieldInspector}
            setOpenAddFieldInspector={setOpenAddFieldInspector}
            workPackageId={CurrentUserSelected.workPackageId}
          />
        </div>
      )}
      {openAssignInspector && (
        <AssignInspector
          title="Assign Inspector"
          isUpdateInspector={false}
          openAssignInspector={openAssignInspector}
          setOpenAssignInspector={setOpenAssignInspector}
          fieldInspectorList={[
            {
              fieldInspectorName: 'Assigned To Me',
              fieldInspectorId: CurrentUserSelected?.currentUser?.inspectorId,
            },
            ...JSON.parse(JSON.stringify(inspectorData)),
          ]}
          leadInspectorId={Number(getValueFromLocalStorage('inspectorId'))}
          inspectionLevelComment=""
          inspectionId={0}
          isAssignToLead={isAssignToLead}
        />
      )}
      {openReAssignInspector &&
        (CurrentUserSelected.isAdhoc ? (
          <AdhocBulkInspectionReAssignment
            open={openReAssignInspector}
            onClose={() => setOpenReAssignInspector(false)}
            taskIds={taskIds}
          />
        ) : (
          <BulkReAssignInspector
            openAssignInspector={openReAssignInspector}
            setOpenAssignInspector={setOpenReAssignInspector}
            fieldInspectorList={[...JSON.parse(JSON.stringify(inspectorData))]}
            taskId={taskIds}
            taskList={taskList}
            isAssignToLead={isAssignToLead}
          />
        ))}
      {open && (
        <InspectionListSortBy
          open={open}
          tabValue={tabValue}
          anchorEl={anchorEl}
          handleClose={handleClose}
          inspectionId={inspectionId}
          inspectionName={inspectionName}
          needAttention={needAttention}
          inspectionLevel={inspectionLevel}
          handleInspectionIdClick={handleInspectionIdClick}
          handleInspectionNameClick={handleInspectionNameClick}
          handleNeedAttentionClick={handleNeedAttentionClick}
          handleInspectionLevelClick={handleInspectionLevelClick}
          isAssignToLead={isAssignToLead}
        />
      )}

      {openAssignToMeDialog && (
        <AssignToMeDialog
          open={openAssignToMeDialog}
          onClose={() => setOpenAssignToMeDialog(false)}
          isAssignToLead={isAssignToLead}
        />
      )}

      {openBulkSignAndStamp && (
        <BulkSignAndStamp
          open={openBulkSignAndStamp}
          onClose={() => setOpenBulkSignAndStamp(false)}
          taskList={taskList}
          inspectionList={inspectionList}
        />
      )}

      {offlineDialogBox && (
        <OfflineDialog open={offlineDialogBox} onClose={() => setOfflineDialogBox(false)} />
      )}
    </>
  );
};

export default observer(InspectionListTabs);
